import { Link } from "react-router-dom";
import { CheckCircle2, CircleAlert } from "lucide-react";
import { SectionTitle, Spinner } from "@/components/common";
import { useLoad, useTrip } from "@/context/TripContext";
import { fmtDate, money } from "@/lib/api";

function Readiness() {
  const { tid } = useTrip();
  const [r] = useLoad(`/trips/${tid}/readiness`);
  if (!r) return <Spinner />;
  return (
    <div className="card p-5" data-testid="readiness-card">
      <div className="flex items-baseline justify-between"><h3 className="text-lg font-bold">Trip readiness</h3><span className="font-mono text-sm text-slate-500" data-testid="readiness-count">{r.done}/{r.total} clear</span></div>
      <p className="text-xs text-slate-500">Based only on what's been entered in Triply.</p>
      <ul className="mt-4 space-y-2">
        {r.checks.map((c) => (
          <li key={c.key}>
            <Link to={`/trips/${tid}/${c.tab}`} className="flex items-start gap-3 rounded-xl p-2 hover:bg-slate-50" data-testid={`readiness-${c.key}`}>
              {c.ok ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" /> : <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" />}
              <div className="min-w-0"><p className="text-sm font-semibold">{c.label}</p>
                <p className="truncate text-xs text-slate-500">{c.ok ? c.detail : `${c.detail}: ${c.items.join(", ")}`}</p></div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MoneySnap() {
  const { tid, cur, isOrg } = useTrip();
  const [f] = useLoad(`/trips/${tid}/finance`);
  if (!f) return null;
  const rows = isOrg
    ? [["Trip total", f.trip_total_minor], ["Recorded paid", f.collected_minor], ["Outstanding", f.outstanding_minor], ["Overdue", f.overdue_minor]]
    : [["My total", f.me?.total_minor], ["Recorded paid", f.me?.paid_minor], ["Remaining", f.me?.balance_minor], ["Overdue", f.me?.overdue_minor]];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="money-snapshot">
      {rows.map(([l, v], i) => (
        <div key={l} className="card p-4"><p className="text-xs font-medium uppercase tracking-wider text-slate-500">{l}</p>
          <p className={`mt-1 font-mono text-lg font-semibold ${i === 3 && v > 0 ? "text-rose-600" : i === 2 ? "text-orange-600" : ""}`}>{money(v, cur)}</p></div>
      ))}
      {!isOrg && f.me?.next_due && <p className="col-span-2 text-sm text-slate-600 sm:col-span-4">Next: {money(f.me.next_due.amount_minor, cur)} due {fmtDate(f.me.next_due.due_date)}</p>}
    </div>
  );
}

function UpNext() {
  const { tid } = useTrip();
  const [it] = useLoad(`/trips/${tid}/itinerary`);
  const [ann] = useLoad(`/trips/${tid}/announcements`);
  const items = (it?.items || []).filter((i) => i.plan_id === it.current_plan_id).slice(0, 4);
  return (
    <div className="space-y-4">
      <div className="card p-5"><h3 className="font-bold">Coming up</h3>
        {items.length === 0 ? <p className="mt-2 text-sm text-slate-500">No itinerary items yet.</p> : items.map((i) => (
          <div key={i.item_id} className="mt-3 flex gap-3 text-sm"><span className="w-24 shrink-0 font-mono text-slate-500">{fmtDate(i.date)}</span><span className="font-medium">{i.title}</span></div>
        ))}</div>
      {ann?.[0] && <div className="card border-l-4 border-l-orange-500 p-5" data-testid="latest-announcement"><p className="text-xs font-semibold uppercase text-orange-600">Latest announcement</p><p className="mt-1 font-semibold">{ann[0].title}</p><p className="mt-1 line-clamp-3 text-sm text-slate-600">{ann[0].body}</p></div>}
    </div>
  );
}

export default function Overview() {
  const { trip } = useTrip();
  return (
    <div className="space-y-6">
      <SectionTitle title="Overview" sub={trip.description || "Your trip at a glance."} />
      <MoneySnap />
      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]"><Readiness /><UpNext /></div>
    </div>
  );
}
